import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { ApplicationsStateService } from "../services/common/applications-state.service";
import { MatTableDataSource } from "@angular/material/table";
import { MatIconModule } from "@angular/material/icon";
import { ChatContainerComponent } from "../chat/chat-container/chat-container.component";
import { DashboardService } from "../dashboard/dashboard.service";
import { ApplicationDetail } from "../dashboard/dashboard.models";
import { UserContextService } from "../services/common/user-context.service";
import { Role } from "../models/authorization/role.enum";

import { DataGridComponent } from "../shared/data-grid/data-grid.component";
import { ApplicationsSkeletonComponent } from "./applications-skeleton/applications-skeleton.component";

interface ColumnDef {
  key: string;
  label: string;
  filterable?: boolean;
  type?: "text" | "number" | "date" | "custom" | "json-array";
  customTemplate?: TemplateRef<any>;
  width?: string;
  cellAlign?: string;
  accessor?: (item: any) => any;
  tooltip?: string;
  cellTooltip?: (item: any) => string;
}

interface FilterItem {
  column: string;
  value: string;
}

const CMDB_OUTCOME_TOOLTIP = [
  'Modernize: Needs modernization based on CMDB signals (legacy technology, architecture, or technical debt).',
  'Already Modern: Current platform/architecture is modern and does not require major modernization now.',
  'Excluded: Out of scope for modernization (for example: SaaS/COTS, not in use, sensitive data, or end-of-life).'
].join('\n\n');
const CMDB_CONFIDENCE_LEVEL_OPTIONS = ['Low', 'Medium', 'High', 'Unknown'];
const ASSESSMENT_CONFLICT_OPTIONS = ['Conflict Detected', 'No Conflict', 'Not Assessed'];
const SOURCE_CODE_ASSESSMENT_OPTIONS = ['Source Code Assessment Available', 'Source Code Assessment Not Available'];
const REPOSITORY_AVAILABILITY_OPTIONS = ['Available', 'Not Available'];

@Component({
  selector: "modernization-applications",
  templateUrl: "./applications.component.html",
  styleUrls: ["./applications.component.css"],
  imports: [DataGridComponent, FormsModule, MatIconModule, ChatContainerComponent, ApplicationsSkeletonComponent],
})

export class ApplicationsComponent implements OnInit, AfterViewInit, OnDestroy {
  canStartAssessment: boolean = false;
  canReassign: boolean = false;

  isLoading: boolean = false;
  isGridLoading: boolean = false;
  page = 1;
  pageSize = 10;
  totalItems = 0;

  // Filter properties
  filters: FilterItem[] = [];
  columnFiltersMap: { [key: string]: string } = {};
  filterableColumns: ColumnDef[] = [];
  assignedByOM: boolean | null | 'showMyWork' = null;
  showRejectedApps: boolean | null = null;
  showMyWork: boolean = false;
  assessmentstatus: string = '';
  filterOptions: { [column: string]: string[] } = {};
  filtersLoaded = false;
  strategystatus: string = '';
  type: string = '';
  priority: string = '';

  displayedColumns: ColumnDef[] = [
    { key: "appId", label: "App ID", filterable: true, width: "100px" },
    { key: "name", label: "App Name", filterable: true, width: "260px" },
    {
      key: "businessCriticality",
      label: "Business Criticality",
      filterable: true,
      width: "100px",
      accessor: (row: any) => {
        if (row.businessCriticality) return row.businessCriticality;
        // Client-side fallback: compute max tier from individual fields.
        // Normalise by stripping spaces so both "Tier3" and "Tier 3" match.
        const vals = [row.availability, row.confidentiality, row.integrity]
          .map((v: string | null) => (v || '').replace(/\s+/g, '').toLowerCase());
        if (vals.includes('tier3')) return 'Tier3';
        if (vals.includes('tier2')) return 'Tier2';
        if (vals.includes('tier1')) return 'Tier1';
        return '';
      }
    },
    // Application disposition and BusAppHealth fields (after Business Criticality)
    { key: "plannedDisposition", label: "Planned Disposition", filterable: true, width: "160px" },
    { key: "consumedByBusinessUnit", label: "Consumed By Business Unit", filterable: true, width: "210px" },
    {
      key: "users", label: "Users", filterable: true, width: "100px", accessor: (item: any) => {
        if (item.users != null) {
          const parts = String(item.users).split('.');
          return parts.length > 0 ? parts[0] : '';
        }
        return '';
      }
    },
    {
      key: "tcoCost", label: "TCO Cost", filterable: true, width: "120px", accessor: (item: any) => {
        if (item.tcoCost != null) {
          const formatted = item.tcoCost.toLocaleString('en-US', { maximumFractionDigits: 2 });
          const parts = formatted.split('.');
          return '$' + (parts.length > 0 ? parts[0] : '');
        }
        return '';
      }
    },
    { key: "incidents", label: "Incidents", filterable: true, width: "110px", accessor: (item: any) => item.incidents != null ? Math.round(item.incidents).toString() : '' },
    { key: "digitalPlatform", label: "Digital Platform", filterable: true, width: "150px" },
    { key: "repositoryAvailabilityStatus", label: "Repository", filterable: true, width: "130px" },
    {
      key: "lightweightCodeAssessmentScore",
      label: "Lightweight Legacy Score",
      filterable: true,
      width: "170px",
      cellAlign: "center",
      tooltip: "AI-generated lightweight source-code legacy score (0-100). Not available until lightweight assessment completes.",
      accessor: (item) => item.lightweightCodeAssessmentScore ?? 'N/A'
    },

    // CMDB assessment indicators (mapped from existing API fields)
    {
      key: "cmdbAnalysisResult",
      label: "Analysis Result",
      filterable: true,
      width: "150px",
      tooltip: CMDB_OUTCOME_TOOLTIP,
      cellTooltip: (item: any) => this.getOutcomeMeaning(item?.cmdbAnalysisResult)
    },
    { key: "cmdbConfidenceLevel", label: "Confidence Level", filterable: true, width: "140px" },
    { key: "cmdbValidationNeeded", label: "CMDB Validation", filterable: false, width: "160px" },
    { key: "assessmentConflictStatus", label: "Assessment Conflict", filterable: true, width: "150px" },
    { key: "cmdbKeyReason", label: "Key Reason", filterable: true, width: "150px" },
    { key: "cmdbRecommendation", label: "Recommendation", filterable: true, width: "190px" },
    { key: "dataSourcesUsed", label: "Data Sources Used", filterable: false, width: "230px" },

    { key: "sourceCodeAssessmentAvailability", label: "Source Code Assessment", filterable: true, width: "170px" },
    { key: "codeComplexity", label: "Analysis Result", filterable: true, width: "150px" },
    { key: "codeKeyReason", label: "Key Reason", filterable: true, width: "160px" },
    { key: "assignedRole", label: "Assigned To", filterable: true, width: "150px" },

    { key: "action", label: "Action", filterable: false, width: "200px" },
  ];

  aiColumnGroups = [
    { label: 'AI - CMDB and Lightweight Code', keys: ['lightweightCodeAssessmentScore', 'cmdbAnalysisResult', 'cmdbConfidenceLevel', 'cmdbValidationNeeded', 'assessmentConflictStatus', 'cmdbKeyReason', 'cmdbRecommendation', 'dataSourcesUsed'], borderLeft: true, colBorderLeft: true, colBorderRight: false },
    { label: 'AI - Code Complexity', keys: ['sourceCodeAssessmentAvailability', 'codeComplexity', 'codeKeyReason'], borderLeft: true, borderRight: true, colBorderLeft: true, colBorderRight: true },
  ];

  displayedColumnsKeys = this.displayedColumns.map((col) => col.key);
  // Top-row grouped headers computed from displayedColumns
  groupHeaders: { label: string; colspan: number }[] = [];
  applications = new MatTableDataSource<ApplicationDetail>([]);
  gridActions: any[] = [];
  dataGridConfig: any = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
    showFirstLastButtons: true,
    showSearch: false,
    showColumnFilters: true,
    enableSorting: true,
    stickyHeader: false,
    rowClass: (row: any) => row.isRejected ? 'rejected-row' : '',
  };
  isOperationsManager: boolean = false;
  isPlatformUser: boolean = false;
  isAMORETeam: boolean = false;

  @ViewChild('actionTemplate') actionTemplate!: TemplateRef<any>;
  @ViewChild('cmdbValidationTemplate') cmdbValidationTemplate!: TemplateRef<any>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dashboardService: DashboardService,
    private userContextService: UserContextService,
    private applicationsStateService: ApplicationsStateService,
  ) { }

  ngOnInit(): void {
    this.isLoading = true;

    const savedState = this.applicationsStateService.restore();
    if (savedState) {
      this.filters = savedState.filters;
      this.columnFiltersMap = savedState.columnFilters;
      this.page = savedState.page;
      this.pageSize = savedState.pageSize;
      this.dataGridConfig = { ...this.dataGridConfig, pageSize: savedState.pageSize };
      this.assignedByOM = savedState.assignedByOM;
      this.showMyWork = savedState.showMyWork ?? false;
      this.showRejectedApps = savedState.showRejectedApps ?? null;
      this.route.queryParams.subscribe(params => {
        this.assessmentstatus = !savedState.assessmentstatus ? params['assessmentStatus'] : savedState.assessmentstatus;
        this.strategystatus = !savedState.strategyStatus ? params['strategyStatus'] : savedState.strategyStatus;
        this.type = savedState.type || params['type'] || '';
        this.priority = savedState.priority || params['priority'] || '';
      });
      this.applicationsStateService.clear();
    }
    else {
      this.route.queryParams.subscribe(params => {
        this.assessmentstatus = params['assessmentStatus'];
        this.strategystatus = params['strategyStatus'];
        this.type = params['type'] || '';
        this.priority = params['priority'] || '';
      });
    }

    const insertIndexBeforeActions = () => this.displayedColumns.length - 2;
    const hasColumn = (key: string) => this.displayedColumns.some(c => c.key === key);
    const insertColumnAt = (index: number, col: ColumnDef) => {
      if (!hasColumn(col.key)) {
        this.displayedColumns.splice(index, 0, col);
      }
    };
    const insertColumnBeforeActions = (col: ColumnDef) => insertColumnAt(insertIndexBeforeActions(), col);

    const hasOperationsManagerLikeRole = this.userContextService.userRoleSubject.value.includes(Role.OperationsManager)
      || this.userContextService.userRoleSubject.value.includes(Role.EnterpriseLead)
      || this.userContextService.userRoleSubject.value.includes(Role.ProjectLead);

    this.canStartAssessment = this.userContextService.userRoleSubject.value.includes(Role.PlatformUser
    ) || hasOperationsManagerLikeRole;
    if (hasOperationsManagerLikeRole) {
      insertColumnBeforeActions({ key: "assignedRole", label: "Assigned To", filterable: false, width: "110px" });
      this.canReassign = true;
      this.isOperationsManager = true;
    }

    if (this.userContextService.userRoleSubject.value.includes(Role.PlatformUser)) {
      this.isPlatformUser = true;
    }

    if (this.userContextService.userRoleSubject.value.includes(Role.AMORETeam)
      || this.userContextService.userRoleSubject.value.includes(Role.ProjectLead)) {
      const assignmentDateCol: ColumnDef = { key: "assignmentDate", label: "Assignment Date", filterable: false, width: "120px" };

      // insertColumnBeforeActions(notesCol);
      insertColumnBeforeActions(assignmentDateCol);

      this.isAMORETeam = true;
    }

    this.displayedColumnsKeys = this.displayedColumns.map((col) => col.key);
    this.filterableColumns = this.displayedColumns.filter(col => col.filterable);

    // Compute grouped headers (CMDB / Code Analysis spans) after columns finalised
    this.computeGroupHeaders();
    this.fetchApplications();

    // Load filter options for dropdown filters
    this.dashboardService.getFilterOptions(this.assessmentstatus, this.strategystatus, this.type, this.priority).subscribe({
      next: (options) => {
        {
          this.filterOptions = {
            ...options,
            lightweightCodeAssessmentScore: ['0-20', '21-40', '41-60', '61-80', '81-100'],
            cmdbConfidenceLevel: Array.from(new Set([
              ...CMDB_CONFIDENCE_LEVEL_OPTIONS,
              ...(options.cmdbConfidenceLevel || [])
            ])),
            assessmentConflictStatus: Array.from(new Set([
              ...ASSESSMENT_CONFLICT_OPTIONS,
              ...(options.assessmentConflictStatus || [])
            ])),
            sourceCodeAssessmentAvailability: Array.from(new Set([
              ...SOURCE_CODE_ASSESSMENT_OPTIONS,
              ...(options.sourceCodeAssessmentAvailability || [])
            ])),
            repositoryAvailabilityStatus: Array.from(new Set([
              ...REPOSITORY_AVAILABILITY_OPTIONS,
              ...(options.repositoryAvailabilityStatus || [])
            ]))
          };
          this.filtersLoaded = true;
        }
      },
      error: (err) => { console.error('Failed to load filter options', err); }
    });
  }

  private computeGroupHeaders(): void {
    const cmdbKeys = new Set(["cmdbAnalysisResult", "cmdbConfidenceLevel", "cmdbValidationNeeded", "assessmentConflictStatus", "cmdbScore", "cmdbKeyReason", "cmdbRecommendation", "dataSourcesUsed"]);
    const codeKeys = new Set(["sourceCodeAssessmentAvailability", "codeComplexity", "codeScore", "codeKeyReason"]);

    const headers: { label: string; colspan: number }[] = [];
    let i = 0;
    while (i < this.displayedColumns.length) {
      const key = this.displayedColumns[i].key;
      if (cmdbKeys.has(key)) {
        let count = 0;
        while (i + count < this.displayedColumns.length && cmdbKeys.has(this.displayedColumns[i + count].key)) {
          count++;
        }
        headers.push({ label: 'AI Assessment Indicators - CMDB', colspan: count });
        i += count;
        continue;
      }

      if (codeKeys.has(key)) {
        let count = 0;
        while (i + count < this.displayedColumns.length && codeKeys.has(this.displayedColumns[i + count].key)) {
          count++;
        }
        headers.push({ label: 'AI Assessment Indicators - Code Analysis', colspan: count });
        i += count;
        continue;
      }

      // Single-column header
      headers.push({ label: this.displayedColumns[i].label, colspan: 1 });
      i++;
    }

    this.groupHeaders = headers;
  }

  ngAfterViewInit(): void {
    const actionCol = this.displayedColumns.find(col => col.key === 'action')!;
    actionCol.customTemplate = this.actionTemplate;
    const cmdbValidationCol = this.displayedColumns.find(col => col.key === 'cmdbValidationNeeded');
    if (cmdbValidationCol) {
      cmdbValidationCol.customTemplate = this.cmdbValidationTemplate;
    }
  }

  fetchApplications(showFullPageLoader: boolean = false): void {
    if (showFullPageLoader) {
      this.isLoading = true;
    }

    this.dashboardService.getApplicationsPortfolio(this.pageSize, this.page, this.filters, this.assignedByOM === 'showMyWork' ? undefined : (this.assignedByOM ?? undefined), this.assessmentstatus, '', this.strategystatus, this.type, this.priority, this.showRejectedApps ?? undefined, this.showMyWork || undefined).subscribe({
      next: (response) => {
        {
          this.isLoading = false;
          this.isGridLoading = false;
          // Map API fields to table-friendly/computed fields used by the template
          const mapped = (response.data || []).map(item => {
            const mappedItem: any = { ...item };
            mappedItem.assignedPodId = item.assignedPodId ?? null;

            // CMDB indicators mapped from existing API properties
            mappedItem.cmdbAnalysisResult = item.outcome ?? 'N/A';
            mappedItem.cmdbConfidenceLevel = item.cmdbConfidenceLevel ?? 'Unknown';
            mappedItem.cmdbValidationNeeded = mappedItem.cmdbConfidenceLevel === 'Low' ? 'Yes' : 'No';
            mappedItem.assessmentConflictStatus = item.assessmentConflictStatus ?? 'Not Assessed';
            mappedItem.sourceCodeAssessmentAvailability = item.sourceCodeAssessmentAvailability ?? 'Source Code Assessment Not Available';
            mappedItem.repositoryAvailabilityStatus = item.repositoryAvailabilityStatus ?? 'Not Available';
            mappedItem.cmdbScore = item.readinessScore ?? '-';
            mappedItem.cmdbKeyReason = item.aiKeyReason ?? '-';
            mappedItem.cmdbRecommendation = item.aiRecommendation ?? '-';
            mappedItem.dataSourcesUsed = this.getDataSourcesUsedLabel(mappedItem);

            // Code analysis indicators from backend
            const status = item.codeAnalysisStatus;
            mappedItem.codeAnalysisQueuePosition = item.codeAnalysisQueuePosition ?? null;
            if (status === 'Completed') {
              mappedItem.codeComplexity = item.codeAnalysisComplexity ?? '-';
              mappedItem.codeScore = item.codeAnalysisScore ?? '-';
              mappedItem.codeKeyReason = item.codeAnalysisKeyReason ?? '-';
            } else if (status === 'Pending' || status === 'Running') {
              const statusLabel = status === 'Pending' && item.codeAnalysisQueuePosition
                ? `Queued #${item.codeAnalysisQueuePosition}`
                : status;
              mappedItem.codeComplexity = statusLabel;
              mappedItem.codeScore = statusLabel;
              mappedItem.codeKeyReason = statusLabel;
            } else if (status === 'Failed') {
              mappedItem.codeComplexity = 'Failed';
              mappedItem.codeScore = 'Failed';
              mappedItem.codeKeyReason = '-';
              mappedItem.codeAnalysisErrorMessage = item.codeAnalysisErrorMessage ?? null;
            } else {
              mappedItem.codeComplexity = '-';
              mappedItem.codeScore = '-';
              mappedItem.codeKeyReason = '-';
            }

            // BusAppHealth fields
            mappedItem.users = item.users != null ? Math.round(item.users) : null;
            mappedItem.tcoCost = item.tcoCost != null ? Math.round(item.tcoCost * 100) / 100 : null;
            mappedItem.incidents = item.incidents != null ? Math.round(item.incidents) : null;

            return mappedItem;
          });

          this.applications.data = mapped;
          this.totalItems = response.totalRecords;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.isGridLoading = false;
        console.error("Error fetching applications data:", error);
      },
    });
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  onPageChange(event: { pageIndex: number, pageSize: number }): void {
    this.isGridLoading = true;
    this.pageSize = event.pageSize;
    this.page = event.pageIndex + 1;
    this.fetchApplications();
  }

  onSortChange(sortState: { column: string; direction: 'asc' | 'desc' | '' }): void {
    if (sortState.direction) {
      this.applications.data = [...this.applications.data].sort((a, b) => {
        const aValue = (a as any)[sortState.column] ?? "";
        const bValue = (b as any)[sortState.column] ?? "";
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortState.direction === "asc" ? comparison : -comparison;
      });
    }
  }

  onColumnFilterChange(event: { column: string; value: string }): void {
    const trimmedValue = event.value.trim();

    if (trimmedValue) {
      // Check if filter for this column already exists
      const existingFilterIndex = this.filters.findIndex(f => f.column === event.column);

      if (existingFilterIndex !== -1) {
        // Update existing filter
        this.filters[existingFilterIndex].value = trimmedValue;
      } else {
        // Add new filter
        this.filters.push({
          column: event.column,
          value: trimmedValue
        });
      }
      this.columnFiltersMap[event.column] = trimmedValue;
    } else {
      // Remove filter if value is empty
      this.filters = this.filters.filter(f => f.column !== event.column);
      delete this.columnFiltersMap[event.column];
    }

    // Reset to first page and fetch with filters
    this.page = 1;
    this.isGridLoading = true;
    this.fetchApplications();
  }

  getColumnLabel(columnKey: string): string {
    const column = this.displayedColumns.find(col => col.key === columnKey);
    return column ? column.label : columnKey;
  }

  onViewClick(row: ApplicationDetail): void {
    // Persist current grid state so it can be restored when navigating back
    this.applicationsStateService.save({
      filters: this.filters,
      columnFilters: this.columnFiltersMap,
      page: this.page,
      pageSize: this.pageSize,
      assignedByOM: this.assignedByOM,
      showMyWork: this.showMyWork,
      assessmentstatus: this.assessmentstatus,
      strategyStatus: this.strategystatus,
      type: this.type,
      priority: this.priority,
      showRejectedApps: this.showRejectedApps,
    });
    // Navigate using TrackedBusinessApplicationId
    const trackedBusinessApplicationId = row.trackedBusinessApplicationId;
    this.router.navigate(["/assessment", trackedBusinessApplicationId]);
  }

  onTechnicalOwnerFilterChange(): void {
    this.showMyWork = this.assignedByOM === 'showMyWork';
    this.page = 1;
    this.isGridLoading = true;
    this.fetchApplications();
  }

  onShowRejectedAppsFilterChange(): void {
    this.page = 1;
    this.isGridLoading = true;
    this.fetchApplications();
  }

  ngOnDestroy(): void {
  }

  private getOutcomeMeaning(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'modernize') {
      return 'Modernize: Needs modernization based on CMDB signals such as legacy architecture, hosting profile, or technical debt.';
    }
    if (normalized === 'already modern') {
      return 'Already Modern: The application appears modern in CMDB and does not currently require major modernization.';
    }
    if (normalized === 'excluded') {
      return 'Excluded: The application is out of scope for modernization, for example SaaS/COTS, not in use, sensitive data, or end-of-life.';
    }
    return '';
  }

  private getDataSourcesUsedLabel(item: any): string {
    const sources = ['CMDB Metadata'];

    if (item.sourceCodeAssessmentAvailability === 'Source Code Assessment Available') {
      sources.push('Source Code Assessment');
    }

    if (item.assessmentConflictStatus !== 'Not Assessed') {
      sources.push('Lightweight Code Validation');
    }

    if (item.tcoCost != null) {
      sources.push('Cost Signals');
    }

    if (item.users != null || item.incidents != null) {
      sources.push('Operational Signals');
    }

    return sources.join(', ');
  }
}
